import { Weapon } from '../Weapon.js';
import { WEAPONS } from '../../../../config/constants.js';

/**
 * Tuning Fork (BULLET-HELL.md §D7, the champion promotion) — Tine's starter.
 *
 * Two prongs, thrown apart and homing back onto the same target, so every shot
 * visibly *converges*. That is the champion's whole image restated in her
 * opening weapon: two tones, one note. It is a pair of weak homing shots and
 * nothing more clever than that — the convergence is what the weapon says, not
 * a mechanic the player has to service.
 *
 * Deliberately NOT coupled to Attunement. Every starter in this roster is a
 * plain data-driven weapon usable by anyone (see data/characters.js), and a
 * fork that only worked in Tine's hands would break both the pickup pool and
 * the rule that a champion's signature is the thing that makes them play
 * differently.
 *
 * Levels add a prong pair rather than raising per-shot damage much: more
 * lines converging on the same point is the shape that stays legible.
 */
export class TuningFork extends Weapon {
  constructor(config = WEAPONS.tuningFork) {
    super(config);
  }

  /** Prongs per cast: 2, plus a pair at levels 4 and 7. */
  get prongCount() {
    return 2 + (this.level >= 4 ? 2 : 0) + (this.level >= 7 ? 2 : 0);
  }

  _shotDamage() {
    return this.config.damage + this.level * 1.5;
  }

  fire(origin, target, pool) {
    const aim = target
      ? Math.atan2(target.y - origin.y, target.x - origin.x)
      : Math.atan2(origin.facing?.y ?? -1, origin.facing?.x ?? 0);
    const count = this.prongCount;
    const spread = this.config.prongSpread;
    for (let i = 0; i < count; i++) {
      // Paired outward: prong 0 goes left, prong 1 right, and each added pair
      // opens one step wider — so the fan is always symmetric about the aim and
      // the two halves arrive together.
      const step = Math.floor(i / 2) + 1;
      const side = i % 2 === 0 ? -1 : 1;
      const angle = aim + side * spread * 0.5 * step;
      pool.fire({
        x: origin.x,
        y: origin.y,
        vx: Math.cos(angle) * this.config.projectileSpeed,
        vy: Math.sin(angle) * this.config.projectileSpeed,
        radius: this.config.projectileRadius,
        damage: this._shotDamage(),
        hits: 1,
        lifetime: this.config.lifetime,
        color: this.config.color,
        // Homing is what closes the fan again. Without a target the prongs
        // simply fly the way they were thrown, which is the correct behaviour
        // in an empty room and what headless tests see.
        homing: !!target,
        turnRate: this.config.turnRate,
        target,
      });
    }
  }
}
